import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CoreConfigService } from '@core/services/config.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../../../../core/service';
import { RegisterRequest } from '../../../../core/requests/RegisterRequest';
import { confirmPassword, createPasswordStrengthValidator } from '../../../../core/helpers/custom-validators';
import { CoreTranslationService } from '../../../../../@core/services/translation.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ResponseError } from 'app/core/responses/ResponseError';
import { loginResponseErrors } from 'app/core/responses/LoginResponse';
import { ConsentService } from '../../../../core/service/consent.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [TranslatePipe]
})
export class RegisterComponent implements OnInit, OnDestroy {
  // Public
  public coreConfig: any;
  public passwordTextType = false;
  public registerForm: FormGroup;
  public token: string;
  public logo: string = null;
  public tosDocumentUrl = '';
  public companyName: string = '';
  public gdprUrl = '';
  public mybenefitsTermsUrl = '';
  public privacyPolicyUrl = '';
  // Private
  private _unsubscribeAll: Subject<any>;
  public subdomain: string;

  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: FormBuilder,
    private _coreTranslationService: CoreTranslationService,
    private authenticationService: AuthenticationService,
    private consentService: ConsentService,
    private _route: ActivatedRoute,
    private _router: Router,
    private translate: TranslatePipe,
    private translateService: TranslateService,
    private _toastrService: ToastrService
  ) {
    this.subdomain = this._route.snapshot.params['subdomain'];
    this.authenticationService.currentUserCompanyInfo.subscribe(x => {
      if (x) {
        this.logo = x.pictureUrl;
        this.tosDocumentUrl = x.tosDocumentUrl;
        this.companyName = x.companyName;
      }
    });
    this.token = this._route.snapshot.params.registerToken || '';
    this.registerForm = new FormGroup({
      password: new FormControl('', [Validators.required, (control: AbstractControl) => {
        return createPasswordStrengthValidator(control, "")
      }]),
      confirmPassword: new FormControl('', [Validators.required]),
      mybenefitsTerms: new FormControl(false, [Validators.requiredTrue]),
      gdprConsent: new FormControl(false, [Validators.requiredTrue]),
      privacyPolicy: new FormControl(false, [Validators.requiredTrue])
    },
    {
      validators: [confirmPassword('password', 'confirmPassword')]
    });
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true
        },
        menu: {
          hidden: true
        },
        footer: {
          hidden: true
        },
        customizer: false,
      }
    };
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.registerForm.controls;
  }

  /**
     * Toggle password
     */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  /**
     * On Submit
     */
  onSubmit() {
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }

    const params: RegisterRequest = {
      password: this.f.password.value,
      confirmPassword: this.f.confirmPassword.value,
      activationToken: this.token
    };

    this.authenticationService.register(params)
      .subscribe(
        data => {
          this.onRegisterSuccess(data);
        },
        error => {
          this.onRegisterFail(error);
        });
  }

  public onRegisterSuccess(data: any) {
    this._toastrService.success(
      this.translate.transform('REGISTER.SUCCESS'),
      this.translate.transform('SUCCESS'),
      { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
    );
    // redirect to login
    setTimeout(() => {
      this._router.navigate(['/' + this.subdomain + '/']);
    }, 100);
  }

  public onRegisterFail(error: any) {
    if(error && error.messages) {
      error.messages.forEach((errorItem) => {
        this._toastrService.error(
          this.translate.transform(errorItem),
          this.translate.transform('ERROR'),
          {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
        );
      });
    }
  }
  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
     * On init
     */
  ngOnInit(): void {
    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
    });

    // Fetch consent links from API
    this.consentService.getConsentLinks().subscribe({
      next: (links) => {
        this.gdprUrl = links.gdprUrl;
        this.mybenefitsTermsUrl = links.termsUrl;
        this.privacyPolicyUrl = links.privacyPolicyUrl;
      },
      error: () => {}
    });
  }

  /**
     * On destroy
     */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }

  openNewTab(url: string) {
    window.open(url);
  }
}